// src/portal-app/PortalShell.js
//
// Adaptive shell + bottom-tab navigator (F12b Task 6) — the frame every
// Aqana Manager surface lives in, built to the approved mockup
// (docs/mockups/f12b/manager-liquid-glass.html) with the Task 3 Liquid-Glass
// primitives.
//
//   Phone  (useIsMobile() true)  — ANCHORED frosted bottom tab BAR: 5 tabs,
//     icon + label each, safe-area aware (--safe-bottom), ≥44pt targets.
//     Was a floating inset pill until 2026-08-27; anchored full-bleed now, to
//     match the POS station bar and the platform convention that a tab bar is
//     chrome content passes under rather than a widget floating on the page.
//   Tablet (useIsMobile() false) — the pill is replaced by a left glass RAIL
//     (adaptive-navigation guideline), same 5 tabs, icon + label rows.
//
// Active-tab state is local (useState, default 'home') — the shell owns tab
// selection. Each tab id renders its own landing screen (Task 8 wires up the
// five landings built in Tasks 3-7: HomeTab, InsightsTab, OperationsTab,
// TeamTab, MoreTab — see TAB_SCREENS below). The old HubScreen generic
// renderer is gone.
// Active tab: brand colour + tinted background + weight bump (colour is never
// the only cue). Tokens only; RTL-safe (logical properties throughout);
// reduced-motion inherits LiquidGlass CSS.
//
// W2 Task 2: the shell is wrapped in NavProvider (per-tab push/pop stacks) and
// the content column hosts NavStackHost, which overlays pushed screens above
// the tab root. Pushed screens reuse the .psh-main padding contract (passed as
// contentClassName) so they clear the floating tab pill exactly like roots do
// — the tab bar stays visible on pushed screens (HIG). The root <main> is
// aria-hidden while a screen is pushed (it stays mounted underneath, covered
// by the opaque pushed screen).
import React, { useState } from 'react';
import useIsMobile from '../hooks/useIsMobile';
import { useLanguage } from '../context/LanguageContext';
import { LiquidGlassBackground, GlassCard } from './shell/LiquidGlass';
import { TABS } from './nav/tabs';
import { NavProvider, NavStackHost, useNav } from './nav/NavStack';
import { RangePresetProvider } from './insights/rangeState';
import HomeTab from './tabs/HomeTab';
import InsightsTab from './tabs/InsightsTab';
import OperationsTab from './tabs/OperationsTab';
import TeamTab from './tabs/TeamTab';
import MoreTab from './tabs/MoreTab';

// Task 8: each tab id maps to its own landing screen (Tasks 3-7 built the
// five surfaces). Unknown ids render nothing — preserves the old
// HubScreen(hub=undefined) => null non-crashing fallback.
const TAB_SCREENS = {
    home: HomeTab,
    insights: InsightsTab,
    operations: OperationsTab,
    team: TeamTab,
    more: MoreTab,
};

const CSS = `
.psh-wrap{flex:1;min-height:0;position:relative;display:flex;align-items:stretch;}

/* ---- content column ---- */
/* .psh-content anchors the NavStackHost overlay (absolute inset:0) to the
   content column only — the tablet rail and the phone pill stay outside it. */
.psh-content{position:relative;flex:1;min-width:0;display:flex;align-items:stretch;}
.psh-main{flex:1;min-width:0;overflow-y:auto;box-sizing:border-box;
  padding:calc(18px + var(--safe-top, 0px)) 18px calc(18px + var(--safe-bottom, 0px));}
/* phone: clear the anchored tab bar. 52px tab + 8px top + 8px bottom padding
   = 68px of bar, plus 16px of breathing room so the last row of content is
   not flush against it. The bar adds --safe-bottom itself, so it is counted
   once here too and nowhere else. */
.psh-phone .psh-main{padding-bottom:calc(84px + var(--safe-bottom, 0px));}
.psh-tablet .psh-main{padding-inline:28px;}

/* ---- shared tab button ---- */
.psh-tab{position:relative;display:flex;align-items:center;justify-content:center;
  min-width:44px;min-height:44px;border:none;background:transparent;cursor:pointer;
  font-family:inherit;color:var(--c-muted);-webkit-tap-highlight-color:transparent;
  transition:transform .15s ease,color .18s ease,background .18s ease,box-shadow .18s ease;}
.psh-tab:active{transform:scale(.95);}
.psh-tab svg{width:22px;height:22px;flex:none;}
.psh-on{color:var(--c-greenDark);font-weight:800;
  background:color-mix(in srgb,var(--c-greenLight) 88%,transparent);
  box-shadow:0 2px 10px color-mix(in srgb,var(--c-green) 18%,transparent),
             inset 0 1px 0 color-mix(in srgb,var(--c-surface) 80%,transparent);}

/* ---- phone: ANCHORED bottom tab bar ----
   Was a floating inset pill (inset-inline:20px, bottom:14px, radius 26). It
   is now anchored full-bleed to the bottom edge, matching the POS station bar
   and the platform convention: a tab bar is system chrome that content passes
   under, not a widget floating on the page.

   The card recipe from <GlassCard/> is overridden here rather than dropped —
   the material (blur, veil, specular) is still wanted, only the card SHAPE is
   not: no all-round border, no radius, no drop shadow. What replaces them is a
   top hairline and an upward shadow, so the bar reads as sitting in front of
   the content it covers. */
.psh-tabbar{position:absolute;inset-inline:0;bottom:0;z-index:50;
  padding:8px 10px calc(8px + var(--safe-bottom, 0px));box-sizing:border-box;
  display:flex;align-items:stretch;justify-content:space-around;gap:2px;
  border:none;border-radius:0;
  border-top:1px solid color-mix(in srgb,var(--c-border) 45%,transparent);
  box-shadow:0 -8px 26px color-mix(in srgb,var(--c-ink) 7%,transparent);}
/* GlassCard's ::before is a top-down card sheen; on an anchored bar the lit
   edge is the TOP one, so it becomes a 1px specular rim instead. */
.psh-tabbar::before{inset:0 0 auto 0;height:1px;border-radius:0;opacity:1;
  background:linear-gradient(90deg,transparent 0%,
    color-mix(in srgb,var(--c-surface) 85%,transparent) 50%,transparent 100%);}
.psh-tabbar .psh-tab{flex:1;max-width:120px;min-height:52px;flex-direction:column;gap:3px;
  border-radius:15px;font-size:10.5px;font-weight:700;letter-spacing:.2px;}
@media (max-width:400px){
  .psh-tabbar{gap:3px;padding-inline:5px;}
  .psh-tabbar .psh-tab{font-size:10px;letter-spacing:0;}
}

/* ---- tablet: left rail (adaptive-navigation) ---- */
.psh-rail{flex:none;width:224px;box-sizing:border-box;z-index:50;align-self:stretch;
  margin:calc(16px + var(--safe-top, 0px)) 0 calc(16px + var(--safe-bottom, 0px));
  margin-inline-start:16px;padding:16px 10px;
  display:flex;flex-direction:column;gap:4px;overflow-y:auto;}
.psh-brand{margin:2px 8px 14px;font-size:11px;font-weight:700;letter-spacing:.9px;
  text-transform:uppercase;color:var(--c-muted);}
.psh-rail .psh-tab{flex:none;justify-content:flex-start;gap:12px;min-height:48px;
  padding:0 14px;border-radius:14px;font-size:13.5px;font-weight:700;
  letter-spacing:-.1px;text-align:start;}

@media (prefers-reduced-motion: reduce){
  .psh-tab,.psh-tab:active{transition:none;transform:none;}
}
`;

/* Inline SVG icon set for the 5 tabs — keys match tabs.js `icon`. No emoji. */
const ICON_SVG_PROPS = {
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.9,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
};

const TAB_ICONS = {
    home: (
        <svg {...ICON_SVG_PROPS}>
            <path d="M3 10.5L12 3l9 7.5" />
            <path d="M5 9.5V21h14V9.5" />
            <path d="M9.5 21v-6h5v6" />
        </svg>
    ),
    insights: (
        <svg {...ICON_SVG_PROPS}>
            <path d="M3 21h18" />
            <path d="M6 21v-9" />
            <path d="M11 21V7" />
            <path d="M16 21v-9" />
            <path d="M21 21V4" />
        </svg>
    ),
    operations: (
        <svg {...ICON_SVG_PROPS}>
            <rect x="8" y="2.5" width="8" height="4" rx="1.2" />
            <path d="M16 4.5h2a2 2 0 0 1 2 2V19a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6.5a2 2 0 0 1 2-2h2" />
            <path d="M9 13.5l2 2 4-4.5" />
        </svg>
    ),
    team: (
        <svg {...ICON_SVG_PROPS}>
            <path d="M16 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2" />
            <circle cx="9.5" cy="7" r="4" />
            <path d="M21 21v-2a4 4 0 0 0-3-3.85" />
            <path d="M15 3.15a4 4 0 0 1 0 7.7" />
        </svg>
    ),
    more: (
        <svg {...ICON_SVG_PROPS}>
            <circle cx="5" cy="12" r="1.4" />
            <circle cx="12" cy="12" r="1.4" />
            <circle cx="19" cy="12" r="1.4" />
        </svg>
    ),
};

export default function PortalShell() {
    const [activeId, setActiveId] = useState('home');

    return (
        <NavProvider activeTabId={activeId} onTabChange={setActiveId}>
            {/* Shared Insights range preset (W2 final M1): lives at shell
                level so it survives pops and tab switches, and unmounts —
                resetting — with the shell on sign-out. */}
            <RangePresetProvider>
                <PortalShellBody activeId={activeId} onSelectTab={setActiveId} />
            </RangePresetProvider>
        </NavProvider>
    );
}

// Inner body so it can read useNav() (PortalShell itself renders the
// provider). Owns no state — tab selection stays in PortalShell above.
function PortalShellBody({ activeId, onSelectTab }) {
    const isMobile = useIsMobile();
    const { t } = useLanguage();
    const { depth, resetToRoot } = useNav();

    const tabButtons = TABS.map((tab) => {
        const active = tab.id === activeId;
        return (
            <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={active}
                className={`psh-tab${active ? ' psh-on' : ''}`}
                // HIG: re-tapping the active tab pops its stack to the root
                onClick={() => (active ? resetToRoot() : onSelectTab(tab.id))}
            >
                {TAB_ICONS[tab.icon] || null}
                <span>{t(tab.labelKey, {}, tab.fallback)}</span>
            </button>
        );
    });

    const navLabel = t('portal_nav', {}, 'Portal navigation');

    return (
        <LiquidGlassBackground intensity="calm">
            <style>{CSS}</style>
            <div className={`psh-wrap ${isMobile ? 'psh-phone' : 'psh-tablet'}`}>
                {!isMobile && (
                    <GlassCard
                        as="nav"
                        role="tablist"
                        aria-label={navLabel}
                        className="psh-rail"
                        data-testid="portal-tab-rail"
                    >
                        <p className="psh-brand">{t('manager_portal', {}, 'Manager Portal')}</p>
                        {tabButtons}
                    </GlassCard>
                )}
                <div className="psh-content">
                    <main className="psh-main" aria-hidden={depth > 0 || undefined}>
                        {(() => {
                            const Screen = TAB_SCREENS[activeId];
                            return Screen ? <Screen /> : null;
                        })()}
                    </main>
                    <NavStackHost tabId={activeId} contentClassName="psh-main" />
                </div>
                {isMobile && (
                    <GlassCard
                        as="nav"
                        role="tablist"
                        aria-label={navLabel}
                        className="psh-tabbar"
                        data-testid="portal-tab-bar"
                    >
                        {tabButtons}
                    </GlassCard>
                )}
            </div>
        </LiquidGlassBackground>
    );
}
